// Build same-origin Dagit URLs that authenticate to tenant-api's
// /admin/dagit reverse proxy.
//
// Auth is via a short-lived, HttpOnly, Dagit-scoped session cookie set by
// POST /admin/dagit/session. We NEVER put the JWT in the URL — that leaked
// it to browser history, server/proxy logs, and the Referer header. Call
// ensureDagsterSession() (or use openDagster()) before opening a Dagit link
// so the cookie is in place.
//
// REACT_APP_DAGIT_URL is honoured only for SaaS deployments that still
// route Dagit through vendor-console at a different origin.

import { isDagitEnabled } from './deploymentConfigService';

// Re-exported so call sites gate their buttons on the same source of truth
// this module uses to refuse navigation. Most deployments run with the Dagit
// reverse proxy off (see tenant-api core/config.py DAGIT_PROXY_ENABLED), and
// then it isn't mounted at all — opening a tab would land on a 404.
export { isDagitEnabled };

const DEFAULT_DAGIT_BASE = '/admin/dagit';
const CONFIGURED_BASE = process.env.REACT_APP_DAGIT_URL;

export function dagsterBase() {
  return (CONFIGURED_BASE || DEFAULT_DAGIT_BASE).replace(/\/$/, '');
}

export function dagsterUrl(subPath = '/') {
  const base = dagsterBase();
  const tail = subPath.startsWith('/') ? subPath : `/${subPath}`;
  return `${base}${tail}`;
}

export function dagsterRunUrl(runId) {
  return dagsterUrl(`/runs/${encodeURIComponent(runId)}`);
}

// Exchange the bearer token for the HttpOnly Dagit session cookie. Safe to
// call repeatedly; resolves true on success.
export async function ensureDagsterSession() {
  if (!isDagitEnabled()) {return false;}
  const token = (typeof window !== 'undefined' && localStorage.getItem('authToken')) || '';
  if (!token) {return false;}
  try {
    const resp = await fetch(`${dagsterBase()}/session`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'same-origin',
    });
    return resp.ok;
  } catch (e) {
    return false;
  }
}

// Open a Dagit URL in a new tab, ensuring the session cookie first.
//
// The tab is opened SYNCHRONOUSLY inside the click's user-gesture, BEFORE the
// awaited session POST. Opening after the await pushes window.open outside the
// activation window, so popup blockers eat it (Safari/Firefox block it outright;
// Chrome only allows it if the POST resolves inside its ~5s transient-activation
// window) and the button appears to do nothing. We can't pass 'noopener' (it
// makes window.open return null, leaving no handle to navigate after the await),
// so we sever window.opener manually to keep the reverse-tabnabbing protection.
export async function openDagster(subPath = '/') {
  if (typeof window === 'undefined') {return;}
  // Checked BEFORE window.open: when the proxy is off, opening the tab first
  // and bailing on the session POST would leave the operator staring at a
  // blank tab instead of nothing happening.
  if (!isDagitEnabled()) {return;}
  const win = window.open('', '_blank');
  if (win) {
    try { win.opener = null; } catch (_) { /* older browsers may disallow */ }
  }
  await ensureDagsterSession();
  const url = dagsterUrl(subPath);
  if (win) {
    win.location = url;
  } else {
    // Synchronous open was still blocked — best-effort retry (may no-op).
    window.open(url, '_blank', 'noopener');
  }
}

export async function openDagsterRun(runId) {
  return openDagster(`/runs/${encodeURIComponent(runId)}`);
}
